import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Activity, MessageSquare, Thermometer, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DECISIONS = {
  retour_classe: "Retour en classe",
  repos_infirmerie: "Repos à l'infirmerie",
  appel_parent: "Appel au parent",
  hopital: "Évacuation hôpital",
  renvoi_maison: "Renvoi à la maison",
};

const DECISION_COLORS = {
  retour_classe: "bg-green-100 text-green-800",
  repos_infirmerie: "bg-blue-100 text-blue-800",
  appel_parent: "bg-orange-100 text-orange-800",
  hopital: "bg-red-100 text-red-800",
  renvoi_maison: "bg-amber-100 text-amber-800",
};

export default function InfirmeriePassagesPage() {
  const [visits, setVisits] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    student_id: "",
    date: today,
    motif: "",
    symptoms: "",
    temperature: "",
    care_given: "",
    medications: "",
    decision: "retour_classe",
    follow_up_notes: "",
    notify_parent: true,
  });

  const load = async () => {
    try {
      const [{ data: vs }, { data: ss }] = await Promise.all([
        api.get("/infirmary/visits"),
        api.get("/students"),
      ]);
      setVisits(vs);
      setStudents(ss);
    } catch {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.student_id || !form.motif) return toast.error("Élève et motif obligatoires");
    setSaving(true);
    try {
      await api.post("/infirmary/visits", {
        ...form,
        temperature: form.temperature ? Number(form.temperature) : null,
      });
      toast.success("Passage enregistré" + (form.notify_parent ? " — parent notifié" : ""));
      setOpen(false);
      setForm({ ...form, motif: "", symptoms: "", temperature: "", care_given: "", medications: "", follow_up_notes: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="passages-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Activity className="h-3 w-3" /><span>REGISTRE DES PASSAGES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Passages infirmerie</h1>
          <p className="text-slate-500 mt-1">{visits.length} passage(s) enregistré(s)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="new-visit-btn"><Plus className="h-4 w-4 mr-2" />Nouveau passage</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nouveau passage à l&apos;infirmerie</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Élève *</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({...form, student_id: v})}>
                    <SelectTrigger data-testid="visit-student"><SelectValue placeholder="Choisir un élève..." /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} {s.class_name && `— ${s.class_name}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="visit-date" />
                </div>
              </div>
              <div>
                <Label>Motif *</Label>
                <Input value={form.motif} onChange={e => setForm({...form, motif: e.target.value})} placeholder="Maux de tête, chute, fièvre, blessure légère..." data-testid="visit-motif" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Symptômes</Label>
                  <Textarea value={form.symptoms} onChange={e => setForm({...form, symptoms: e.target.value})} rows={2} data-testid="visit-symptoms" />
                </div>
                <div>
                  <Label>Température (°C)</Label>
                  <div className="relative">
                    <Thermometer className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input type="number" step="0.1" value={form.temperature} onChange={e => setForm({...form, temperature: e.target.value})} className="pl-10" placeholder="37.0" data-testid="visit-temp" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Soins prodigués</Label>
                <Textarea value={form.care_given} onChange={e => setForm({...form, care_given: e.target.value})} rows={2} placeholder="Désinfection, pansement, prise de Doliprane..." data-testid="visit-care" />
              </div>
              <div>
                <Label>Médicaments administrés</Label>
                <Input value={form.medications} onChange={e => setForm({...form, medications: e.target.value})} placeholder="Doliprane 500mg x1" data-testid="visit-meds" />
              </div>
              <div>
                <Label>Décision *</Label>
                <Select value={form.decision} onValueChange={(v) => setForm({...form, decision: v})}>
                  <SelectTrigger data-testid="visit-decision"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DECISIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <input type="checkbox" id="notify" checked={form.notify_parent} onChange={e => setForm({...form, notify_parent: e.target.checked})} className="h-4 w-4" data-testid="visit-notify" />
                <Label htmlFor="notify" className="text-sm font-normal">Notifier le parent (WhatsApp + Email)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="save-visit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Élève</th>
                <th className="px-4 py-3">Motif</th>
                <th className="px-4 py-3">Temp.</th>
                <th className="px-4 py-3">Soins</th>
                <th className="px-4 py-3">Décision</th>
                <th className="px-4 py-3">Parent</th>
              </tr>
            </thead>
            <tbody>
              {visits.map(v => (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`visit-${v.id}`}>
                  <td className="px-4 py-3 font-medium">{v.date}</td>
                  <td className="px-4 py-3"><div className="font-semibold">{v.student_name}</div><div className="text-xs text-slate-400">{v.class_name}</div></td>
                  <td className="px-4 py-3">{v.motif}</td>
                  <td className="px-4 py-3 tabular-nums">{v.temperature ? `${v.temperature}°C` : "-"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs">{v.care_given}</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-1 rounded text-xs font-bold ${DECISION_COLORS[v.decision]}`}>{DECISIONS[v.decision]}</span></td>
                  <td className="px-4 py-3">{v.parent_notified ? <span className="text-xs text-green-600 font-bold">✓ Notifié</span> : <span className="text-xs text-slate-400">—</span>}</td>
                </tr>
              ))}
              {visits.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucun passage enregistré.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
